import React from 'react';
import { useEventHistory, formatDateTime } from '@redbax/shared';
import type { DetectionHistoryItem } from '@redbax/shared';
import {
  Card,
  CardContent,
  Badge,
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  LoadingSkeleton,
  EmptyState,
} from '@redbax/ui-kit';
import { ContentDetectionModal } from './content-detection-modal';
import { PackageDetectionModal } from './package-detection-modal';

const CATEGORY_OPTIONS = [
  { value: '', label: 'All Categories' },
  { value: 'PII', label: 'PII' },
  { value: 'PCI', label: 'PCI' },
  { value: 'Confidential', label: 'Confidential' },
  { value: 'PackageSecurity', label: 'Package Security' },
];

function categoryBadgeVariant(category: string) {
  switch (category) {
    case 'PII':
      return 'info' as const;
    case 'PCI':
      return 'warning' as const;
    case 'Confidential':
      return 'destructive' as const;
    case 'PackageSecurity':
      return 'outline' as const;
    default:
      return 'secondary' as const;
  }
}

function actionBadgeVariant(action: string) {
  switch (action) {
    case 'Block':
    case 'VulnBlock':
      return 'destructive' as const;
    case 'Warn':
    case 'AgeBlock':
      return 'warning' as const;
    case 'Log':
      return 'secondary' as const;
    default:
      return 'outline' as const;
  }
}

function formatRelativeTime(isoString: string): string {
  const now = Date.now();
  const then = new Date(isoString).getTime();
  const diffMs = now - then;

  if (diffMs < 0) return 'just now';

  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return `${seconds}s ago`;

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;

  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days > 1 ? 's' : ''} ago`;

  const months = Math.floor(days / 30);
  return `${months} month${months > 1 ? 's' : ''} ago`;
}

function formatFullTimestamp(isoString: string): string {
  return formatDateTime(isoString);
}

export function EventHistoryTab() {
  const [category, setCategory] = React.useState<string>('');
  const [selectedEvent, setSelectedEvent] = React.useState<DetectionHistoryItem | null>(null);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useEventHistory(
    category || undefined,
  );

  const sentinelRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const allItems = data?.pages.flatMap((p) => p.items) ?? [];

  const isPackageEvent = (item: DetectionHistoryItem) => item.category === 'PackageSecurity';

  return (
    <div>
      <div className="mb-4 flex items-center gap-3">
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-[200px]">
            <span>{CATEGORY_OPTIONS.find((o) => o.value === category)?.label ?? 'All Categories'}</span>
          </SelectTrigger>
          <SelectContent>
            {CATEGORY_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <LoadingSkeleton rows={8} />
      ) : allItems.length === 0 ? (
        <EmptyState
          title="No detection events"
          description="Detection events will appear here as they are captured by your endpoint agents."
        />
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-surface">
                    <th className="px-4 py-3 text-left font-medium text-text-secondary">Time</th>
                    <th className="px-4 py-3 text-left font-medium text-text-secondary">Category</th>
                    <th className="px-4 py-3 text-left font-medium text-text-secondary">Rule</th>
                    <th className="px-4 py-3 text-left font-medium text-text-secondary">Action</th>
                    <th className="px-4 py-3 text-left font-medium text-text-secondary">Target</th>
                    <th className="px-4 py-3 text-left font-medium text-text-secondary">Hostname</th>
                  </tr>
                </thead>
                <tbody>
                  {allItems.map((item) => (
                    <tr
                      key={item.eventId}
                      className="border-b border-border last:border-0 cursor-pointer hover:bg-surface-raised/50 transition-colors"
                      onClick={() => setSelectedEvent(item)}
                    >
                      <td
                        className="px-4 py-3 text-text-primary whitespace-nowrap"
                        title={formatFullTimestamp(item.occurredAt)}
                      >
                        {formatRelativeTime(item.occurredAt)}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={categoryBadgeVariant(item.category)}>{item.category}</Badge>
                      </td>
                      <td className="px-4 py-3 text-text-primary max-w-[200px] truncate">
                        {item.ruleName}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={actionBadgeVariant(item.actionTaken)}>{item.actionTaken}</Badge>
                      </td>
                      <td className="px-4 py-3 text-text-primary max-w-[180px] truncate">
                        {item.targetApplication}
                      </td>
                      <td className="px-4 py-3 text-text-secondary max-w-[150px] truncate">
                        {item.hostname}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <div ref={sentinelRef} className="h-1" />

      {isFetchingNextPage && (
        <div className="mt-4">
          <LoadingSkeleton rows={3} />
        </div>
      )}

      {selectedEvent && !isPackageEvent(selectedEvent) && (
        <ContentDetectionModal
          event={selectedEvent}
          open={true}
          onClose={() => setSelectedEvent(null)}
        />
      )}

      {selectedEvent && isPackageEvent(selectedEvent) && (
        <PackageDetectionModal
          event={selectedEvent}
          open={true}
          onClose={() => setSelectedEvent(null)}
        />
      )}
    </div>
  );
}
